import {ApolloError, useLazyQuery, useMutation, useReactiveVar} from '@apollo/client'
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormLabel,
    Icon,
    Input,
    InputGroup,
    InputRightElement
} from '@chakra-ui/react'
import {yupResolver} from '@hookform/resolvers/yup'
import {remove, uniqBy} from 'lodash-es'
import {useRecoilState} from 'recoil'
import {setCookie} from 'nookies'
import {useTranslation} from 'next-i18next'
import {FiKey, FiCreditCard} from 'react-icons/fi'
import {useEffect, useMemo, useRef, useState} from 'react'
import {FormProvider, useForm } from 'react-hook-form'
import * as yup from 'yup'
import useCustomer from '_app/hooks/useCustomer'
import {Radio} from '_app/components'
import {PLACE_ORDER} from '_app/graphql/mutations/cart/PlaceOrder'
import {SET_PAYMENT_METHOD_ON_CART} from '_app/graphql/mutations/cart/SetPaymentMethodOnCart'
import {CREATE_EMPTY_CART} from '_app/graphql/mutations/cart/CreateEmptyCart'
import {useRouter} from '_app/hooks/useRouter'
import {AvailablePaymentMethod, PlaceOrderOutput, SelectedPaymentMethod} from '_app/types/api'
import {getCategory, getDimension4, getGtmItemListName, getMaterialSwatchLabel, GTM_EVENTS} from '_app/utils/dataLayer'
import toast from '_app/utils/toast'
import useAttributesMetadata from '_app/hooks/useAttributesMetadata'
import {EdroneEvents, getUrl, mapCartData, parseUidsArray, uidToNumber} from '_app/utils/edrone'
import {OrderPayload} from '_app/types/edrone'
import {gtmItemListNamesAtom} from '_app/state/atoms'
import {cartIdVar, emailOnCart} from '../../../_app/cache'
import ProductHelper from '../../../helpers/ProductHelper/ProductHelper'
import LocalStorageHelper from '../../../helpers/LocalStorageHelper/LocalStorageHelper'
import EventsHelper from '../../../helpers/EventsHelper/EventsHelper'
import {APP_ROUTES} from '../../../_app/routes'
import {initializeApollo} from '../../../helpers/ApolloHelper/ApolloHelper'
import {GIFT_CARD_STATUS} from '../../../_app/graphql/queries/giftCards/GiftCardStatus'
import {GIFT_CARD_BALANCE} from '../../../_app/graphql/queries/giftCards/GiftCardBalance'
import {GIFT_CARD_APPLY} from '../../../_app/graphql/queries/giftCards/GiftCardApply'
import {GIFT_CARD_CANCEL} from '../../../_app/graphql/queries/giftCards/GiftCardCancelAuthorization'
import {GetCartDocumentGift} from '../../../_app/graphql/queries/cart/GetCartDocumentGift'

declare const Klarna: any

interface KlarnaAuthorizationRequest {
    billing_address: {
        given_name: string;
        family_name: string;
        email: string;
        street_address: string;
        postal_code: string;
        city: string;
        phone: string;
        country: string;
    };
    shipping_address: {
        given_name: string;
        family_name: string;
        email: string;
        street_address: string;
        postal_code: string;
        city: string;
        phone: string;
        country: string;
    };
}

export const PAYU_PAYMENT_METHODS = [
    {
        isEnabled: true,
        label: 'Twisto - kup teraz zapłać później',
        value: {
            payuMethod: 'dpt',
            payuMethodType: 'PBL',
        },
    },
    {
        isEnabled: true,
        label: 'PayPo - kup teraz zapłać później',
        value: {
            payuMethod: 'dpp',
            payuMethodType: 'PBL',
        },
    },
    {
        isEnabled: true,
        label: 'Karta',
        value: {
            payuMethod: 'c',
            payuMethodType: 'PBL',
        },
    },
    {
        isEnabled: true,
        label: 'BLIK',
        value: {
            payuMethod: 'blik',
            payuMethodType: 'PBL',
        },
    },
]

const PAYMENT_METHODS_TO_BE_REMOVED = ['idea_applepay']
const schema = yup.object().shape({
    selected_payment_method: yup.object().shape({
        code: yup.string().required(),
    }),
})

const PaymentMethodForm = ({data, clientToken, updateSummary}) => {
    const {t} = useTranslation('checkout')
    const customer = useCustomer()
    const router = useRouter()
    const refCardPrice: any = useRef()
    const { locale } = router
    const [showCardPIN, setShowCardPIN] = useState(false)
    const [countDisabled, setCountDisabled] = useState(false)
    const [checkGiftCard, setCheckGiftCard] = useState(false)
    const [cardNumber, setCardNumber] = useState<string|boolean>('')
    const [cardPIN, setCardPIN] = useState<string|boolean>('')
    const [cardPrice, setCardPrice] = useState<string|boolean>('')
    const [cardBalance, setCardBalance] = useState<string|boolean>('')
    const [cardBalancePrice, setCardBalancePrice] = useState<string|boolean>('')
    const [cardOrder, setCardOrder] = useState<string|boolean>(false)
    const [loadingGiftCard, setLoadingGiftCard] = useState<boolean>(false)
    const [loadingCount, setLoadingCount] = useState<boolean>(false)
    const [carrierCode, setCarrierCode] = useState<string|boolean>(false)
    const [getCartDocument] = useLazyQuery(GetCartDocumentGift)
    const [gtmItemListNames, setGtmItemListNames] =
        useRecoilState(gtmItemListNamesAtom)
    const [, setKlarna] = useState(false)
    const [formLocked, setFormLocked] = useState(false)
    const cartId = useReactiveVar(cartIdVar)
    const {metaData} = useAttributesMetadata({})
    const [createEmptyCart] = useMutation(CREATE_EMPTY_CART)
    const handleInactiveCart = async (error: any) => {
        if (error?.message?.toLowerCase().includes('the cart isn\'t active')) {
            const {data: {createEmptyCart: newCartId}} = await createEmptyCart()
            setCookie(null, customer ? 'cartId' : 'guestCartId', newCartId, {path: '/'})
            await router.push('/')
        }
    }
    const mutationErrorOptions = {
        onError: (error: ApolloError) => {
            toast(error)
            handleInactiveCart(error).then(() => {
            })
        },
    }
    const [placeOrder] = useMutation(PLACE_ORDER, {...mutationErrorOptions})
    const [setPaymentMethodOnCart] = useMutation(SET_PAYMENT_METHOD_ON_CART, {
        ...mutationErrorOptions,
    })
    const getAvailablePaymentMethods = (dataCart: any) => {
        const availablePaymentMethods = dataCart?.cart?.available_payment_methods.slice()
        const payuPaymentMethodIndex = availablePaymentMethods?.findIndex(
            (apm: AvailablePaymentMethod) => apm?.code === 'payu_gateway',
        )
        if (payuPaymentMethodIndex >= 0 && availablePaymentMethods?.length > 0 && typeof availablePaymentMethods === 'object') {
            availablePaymentMethods[payuPaymentMethodIndex] = {
                ...availablePaymentMethods[payuPaymentMethodIndex],
                title: 'PayU - szybkie przelewy',
            }
            PAYU_PAYMENT_METHODS?.forEach(paymentMethod => {
                if (paymentMethod?.isEnabled && typeof availablePaymentMethods === 'object') {
                    availablePaymentMethods?.unshift({
                        code: paymentMethod?.value?.payuMethod,
                        title: paymentMethod?.label,
                    })
                }
            })
        }
        PAYMENT_METHODS_TO_BE_REMOVED?.forEach(code => {
            remove(availablePaymentMethods, (apm: AvailablePaymentMethod) => apm?.code === code)
        })
        return uniqBy(availablePaymentMethods, 'code')
    }
    const [availablePaymentMethods, setAvailablePaymentMethods] = useState<any>([])
    const handleMethodPayment = async () => {
        const dataCart: any = await getCartDocument({
            variables: {
                cartId,
            },
        })
        const giftCard = dataCart?.data?.cart?.gift_card?.[0]
        if (giftCard) {
            if (dataCart?.data?.cart?.prices?.grand_total?.value !== 0) {
                setCardOrder('half')
            } else {
                setCardOrder('full')
            }
        }
        const carrierCodeCart = dataCart?.data?.cart?.shipping_addresses?.[0]?.selected_shipping_method?.carrier_code
        setCarrierCode(carrierCodeCart)
        if (dataCart?.data?.cart?.available_payment_methods?.length > 0) {
            let tempData: any = getAvailablePaymentMethods(dataCart?.data)
            if (LocalStorageHelper.get('disableKlarna')) {
                tempData = getAvailablePaymentMethods(dataCart?.data).filter((el: any) => el?.code !== 'klarna_pay_later' && el?.code !== 'lilou_payment_giftcard')
            }
            setAvailablePaymentMethods(tempData)
        }
    }
    useEffect(() => {
        if (cartId) {
            handleMethodPayment()
        }
    }, [data, cartId])
    useEffect(() => {
        if (availablePaymentMethods !== undefined && (availablePaymentMethods ?? [])?.length === 1) {
            setValue('selected_payment_method.code', availablePaymentMethods[0]?.code)
        }
        availablePaymentMethods?.forEach((el: { code: any }) => {
            const code = el?.code
            const isKlarna = code === 'klarna_kp' || code === 'klarna_pay_later'
            if (isKlarna) {
                setKlarna(true)
            }
        })
    }, [availablePaymentMethods])
    const defaultValues = useMemo(
        () => ({
            selected_payment_method: {
                ...data?.cart?.selected_payment_method,
                code: data?.cart?.selected_payment_method?.payu_method ?? data?.cart?.selected_payment_method?.code,
            },
        }),
        [data?.cart],
    )
    const methods = useForm({
        defaultValues,
        resolver: yupResolver(schema),
    })
    const {
        handleSubmit,
        setValue,
        watch,
        formState: {errors},
    } = methods
    const selectPaymentMethodCode = watch('selected_payment_method.code')
    useEffect(() => {
        EventsHelper.scrollToElement('checkout[2]')
    }, [errors])
    const setLastStep = (payload: any, values: any, isCOD: any) => {
        return setPaymentMethodOnCart({
            variables: payload,
            onError: (error) => {
                setFormLocked(false)
                toast({
                    description: `${t('CHANGE_PAYMENT_METHOD')} - ${error}`,
                    status: 'error',
                })
            },
            onCompleted: async () => {
                let res: any = null
                if (cartId) {
                    res = await getCartDocument({
                        variables: {
                            cartId,
                        },
                    })
                }
                return placeOrder({
                    onError: (error) => {
                        setFormLocked(false)
                        toast({
                            description: `${t('CHANGE_PAYMENT_METHOD')} - ${error}`,
                            status: 'error',
                        })
                    },
                    onCompleted: async ({
                        placeOrder,
                    }: {
                        placeOrder: PlaceOrderOutput;
                    }) => {
                        const tempData = res?.data
                        const revenue = tempData?.cart?.prices?.subtotal_with_discount_excluding_tax?.value ?? 0
                        const shipping = tempData?.cart?.shipping_addresses?.[0]?.selected_shipping_method?.amount?.value ?? 0
                        const paymentUrl = placeOrder?.order?.payment_url
                        const addressCart = tempData?.cart?.shipping_addresses[0]
                        const billingCart = tempData?.cart?.billing_address
                        const mapData = mapCartData(tempData)
                        const payloadGTM = {
                            ecommerce: {
                                currencyCode: tempData?.cart?.prices?.currency ?? 'PLN',
                                locale,
                                purchase: {
                                    actionField: {
                                        step: 4 as const,
                                        cartId,
                                        id: placeOrder?.order?.order_number,
                                        has_custom_product: ProductHelper.isPersonalizedProducts(tempData?.cart?.items),
                                        coupon: tempData?.cart?.applied_coupons?.[0]?.code ?? '',
                                        revenue,
                                        shipping,
                                    },
                                    products: tempData?.cart?.items?.map((item: any) => {
                                            const infoResponseProduct = tempData?.cart?.items?.filter((el: any) => el?.product?.sku === item?.product?.sku)[0]
                                            return ({
                                                id: item?.product?.sku,
                                                name: item?.product?.name,
                                                category1: getCategory(item?.product),
                                                category2: 'Nieokreślone',
                                                productIds: mapData(item => uidToNumber(item?.product?.uid)),
                                                price: item?.product?.price_range?.minimum_price?.final_price?.value,
                                                quantity: infoResponseProduct?.quantity,
                                                dimension4: getDimension4(item?.product?.categories),
                                                dimension8: ProductHelper.isPersonalizedProduct(item?.product) ? 'Personalizowany' : 'Standard',
                                                brand: 'Lilou',
                                                variant: getMaterialSwatchLabel(metaData, item?.product.material_swatch?.toString() ?? ''),
                                                item_list_name: getGtmItemListName(gtmItemListNames, item?.product?.sku),
                                                regular_price: item?.product?.price_range?.minimum_price?.regular_price?.value,
                                                omnibus_price: item?.product?.omnibus_price,
                                                item_markdown: locale && item?.product?.omnibus_price && Boolean(item?.product?.omnibus_price < item?.product?.price_range?.minimum_price?.regular_price?.value) ? 'yes' : 'no',
                                            })
                                        }
                                    ),
                                },
                            },
                            payment: {
                                method: payload?.paymentMethod?.code,
                                payuMethod: payload?.payuMethod ?? '',
                                payuMethodType: payload?.payuMethodType ?? '',
                            },
                            shipping: {
                                method: tempData?.cart?.shipping_addresses[0]?.selected_shipping_method?.carrier_code,
                            },
                            user_data: {
                                email: tempData?.cart?.email ?? '',
                                invoice: Boolean(LocalStorageHelper.get('receiveInvoiceWatch')),
                                inpostAdress: LocalStorageHelper.get('inpostAdress'),
                                algoliaTokenUser: LocalStorageHelper.get('algoliaTokenUser'),
                                address: {
                                    first_name: addressCart?.firstname ?? '',
                                    last_name: addressCart?.lastname ?? '',
                                    telephone: addressCart?.telephone ?? '',
                                    street: addressCart?.street?.join(' ') ?? '',
                                    postcode: addressCart?.postcode ?? '',
                                    country: addressCart?.country?.code ?? '',
                                    city: addressCart?.city ?? '',
                                    region: addressCart?.region?.label ?? '',
                                },
                                billing: {
                                    first_name: billingCart?.firstname ?? '',
                                    last_name: billingCart?.lastname ?? '',
                                    telephone: billingCart?.telephone ?? '',
                                    street: billingCart?.street?.join(' ') ?? '',
                                    postcode: billingCart?.country?.postcode ?? '',
                                    country: billingCart?.country?.code ?? '',
                                    city: billingCart?.city ?? '',
                                    region: billingCart?.region?.label ?? '',
                                },
                            },
                        }
                        GTM_EVENTS.purchase(payloadGTM)
                        setGtmItemListNames({})
                        const EdroneOrderPayload: OrderPayload = {
                            email: tempData?.cart?.email,
                            firstName: tempData?.cart?.billing_address?.firstname,
                            lastName: tempData?.cart?.billing_address?.lastname,
                            productSkus: mapData(item => item?.product?.sku),
                            productIds: mapData(item => uidToNumber(item?.product?.uid)),
                            productTitles: mapData(item => item?.product?.name),
                            productImages: mapData(item => item?.product?.image?.url),
                            productUrls: mapData(item => getUrl(item?.product?.url_key)),
                            categoryIds: mapData(item =>
                                parseUidsArray(
                                    item?.product?.categories?.map(category => category?.uid) ??
                                    [],
                                ),
                            ),
                            categoryNames: mapData(item =>
                                item?.product?.categories
                                    ?.map(category => category?.name)
                                    .join('~'),
                            ),
                            productCounts: mapData(item => item?.product?.salable_qty),
                            orderId: placeOrder?.order?.order_number,
                            country: tempData?.cart?.billing_address?.country.code,
                            city: tempData?.cart?.shipping_addresses[0]?.city,
                            baseCurrency:
                                tempData?.cart?.prices?.subtotal_including_tax?.currency ?? '',
                            orderCurrency:
                                tempData?.cart?.prices?.subtotal_including_tax?.currency ?? '',
                            basePaymentValue: (
                                tempData?.cart?.prices?.subtotal_including_tax?.value ?? ''
                            ).toString(),
                            orderPaymentValue: (
                                tempData?.cart?.prices?.subtotal_including_tax?.value ?? ''
                            ).toString(),
                        }
                        EdroneEvents.order(EdroneOrderPayload, router.locale)
                        LocalStorageHelper.set('orderNumber', placeOrder?.order?.order_number)
                        if (!isCOD && paymentUrl) {
                            setFormLocked(false)
                            window.location.href = paymentUrl
                        } else {
                            if (placeOrder?.order?.order_number) {
                                window.location.href = `${APP_ROUTES.CHECKOUT_SUCCESS}.html?order_id=${placeOrder?.order?.order_number}`
                            } else {
                                toast({
                                    description: t('CHANGE_PAYMENT_METHOD'),
                                    status: 'error',
                                })
                            }
                        }
                    },
                })
            },
        })
    }
    const onSubmit = async (values?: {
        selected_payment_method?: SelectedPaymentMethod;
    }) => {
        let dataCart: any = null
        if (cartId) {
            const {data }: any = await getCartDocument({
                variables: {
                    cartId,
                },
            })
            dataCart = data
        }
        const payload: | {
            paymentMethod: { code: string, klarna?: { authorization_token?: string } };
            payuMethod?: string;
            payuMethodType?: string;
        } = { paymentMethod: {code: values?.selected_payment_method?.code || ''} }
        const isCOD = values?.selected_payment_method?.code === 'cashondelivery'
        const ppm = PAYU_PAYMENT_METHODS.find(ppm => ppm.value.payuMethod === values?.selected_payment_method?.code)
        const isKlarna = values?.selected_payment_method?.code === 'klarna_kp' || values?.selected_payment_method?.code === 'klarna_pay_later'
        if (ppm) {
            payload.paymentMethod.code = 'payu_gateway'
            payload.payuMethod = ppm.value.payuMethod
            payload.payuMethodType = ppm.value.payuMethodType
        }
        setFormLocked(true)
        if (isKlarna) {
            if (clientToken) {
                const address = dataCart?.cart?.shipping_addresses[0]
                const authorizationRequest: KlarnaAuthorizationRequest = {
                    billing_address: {
                        given_name: dataCart?.cart?.billing_address?.firstname ?? '',
                        family_name: dataCart?.cart?.billing_address?.lastname ?? '',
                        email: emailOnCart() || '',
                        street_address: dataCart?.cart?.billing_address?.street?.join(' ') ?? '',
                        postal_code: dataCart?.cart?.billing_address?.postcode ?? '',
                        city: dataCart?.cart?.billing_address?.city ?? '',
                        phone: address?.telephone ?? '',
                        country: dataCart?.cart?.billing_address?.country?.label ?? '',
                    },
                    shipping_address: {
                        given_name: address?.firstname ?? '',
                        family_name: address?.lastname ?? '',
                        email: emailOnCart() || '',
                        street_address: `${address?.street[0] ?? ''} ${address?.street[1] ?? ''} ${address?.street[2] ?? ''}`,
                        postal_code: address?.postcode ?? '',
                        city: address?.city ?? '',
                        phone: address?.telephone ?? '',
                        country: address?.country?.code ?? '',
                    },
                }
                if (localStorage?.getItem('receiveInvoiceWatch') !== 'true') {
                    authorizationRequest.billing_address = authorizationRequest?.shipping_address
                }
                const callback = (res: any) => {
                    if (res) {
                        payload.paymentMethod = {
                            code: values?.selected_payment_method?.code || '',
                            klarna: {
                                authorization_token: res?.authorization_token ?? LocalStorageHelper.get('klarnaToken'),
                            },
                        }
                        setFormLocked(false)
                        if (res?.approved) {
                            setFormLocked(true)
                            setLastStep(payload, values, isCOD)
                        }
                    }
                }
                await Klarna.Payments.authorize({}, authorizationRequest, callback)
            }
        }
        else return setLastStep(payload, values, isCOD)
    }
    const handleGifCard = async () => {
        setLoadingGiftCard(true)
        const apolloClient = initializeApollo(null, {locale})
        let valid = true
        if (!cardNumber) {
            valid = false
            toast({ description: 'Uzupełnij numer karty podarunkowej.', status: 'warning' })
        }
        if (!cardPIN) {
            valid = false
            toast({ description: 'Uzupełnij pin karty podarunkowej.', status: 'warning' })
        }
        if (!cardPrice) {
            valid = false
            toast({ description: 'Uzupełnij kwotę rezerwacji środków na karcie podarunkowej.', status: 'warning' })
        }
        if (Number(cardPrice) <= 0) {
            valid = false
            toast({ description: 'Popraw kwotę rezerwacji środków na karcie podarunkowej. Kwota musi być większa niż 0.', status: 'warning' })
        }
        if (valid) {
            const variablesGiftCardStatus = {
                GiftCardStatusInput: {
                    cardNumber,
                }
            }
            const checkStatusCard = await apolloClient.query({ query: GIFT_CARD_STATUS, variables: variablesGiftCardStatus }).then((res) => {
                if (res) {
                    const data = res?.data?.GiftCardStatus?.cardData
                    const isExpired = data?.is_expired
                    if (!data?.is_payment_active) {
                        toast({ description: 'Karta jest nie aktywna. Prosimy o kontakt z działem obsługi klienta.', status: 'error' })
                    } else if (isExpired) {
                        toast({ description: 'Ważność karty wygasła.', status: 'error' })
                    } else {
                        return true
                    }
                }
            })
            if (checkStatusCard) {
                const dataCart: any = await getCartDocument({
                    variables: {
                        cartId,
                    },
                })
                updateSummary()
                const priceOrder = dataCart?.data?.cart?.prices?.grand_total?.value || 0
                if (Number(cardPrice) > Number(priceOrder)) {
                    toast({ description: 'Podana kwota jest za duża. Kwota powinna być równa wartości zamówienia.', status: 'warning' })
                } else {
                    const variablesGiftCardBalance = {
                        GiftCardBalanceInput: {
                            cardNumber,
                            cardPin: cardPIN,
                        }
                    }
                    await apolloClient.query(({query: GIFT_CARD_BALANCE, variables: variablesGiftCardBalance})).then(async (response) => {
                        if (response) {
                            const dataBalance = response?.data?.GiftCardBalance
                            const data = dataBalance?.cardData
                            const success = dataBalance?.success
                            const valueBalance = data?.value_balance
                            if (success) {
                                if (valueBalance >= cardPrice) {
                                    setCardBalancePrice(valueBalance)
                                    const variablesGiftCardApply = {
                                        GiftCardApplyInput: {
                                            quoteMaskedId: cartId,
                                            cardNumber,
                                            cardPin: cardPIN,
                                            discountAmount: cardPrice,
                                            storeId: 1
                                        }
                                    }
                                    await apolloClient.query({query: GIFT_CARD_APPLY, variables: variablesGiftCardApply}).then(async (responseApply) => {
                                        if (responseApply) {
                                            const data = responseApply?.data?.GiftCardApply
                                            const success = data?.success
                                            if (success) {
                                                if (Number(priceOrder) === Number(cardPrice)) {
                                                    setCardOrder('full')
                                                } else {
                                                    setCardOrder('half')
                                                }
                                            } else {
                                                toast({description: 'Niestety nie udało się zarezerwować środków na karcie. Spróbuj ponownie później.', status: 'error'})
                                            }
                                        }
                                    })
                                } else {
                                    valueBalance && setCardBalance(valueBalance)
                                    toast({description: 'Brak wystarczających środków na karcie.', status: 'error'})
                                }
                            } else {
                                toast({description: 'PIN lub numer karty jest nieprawidłowy.', status: 'error'})
                            }
                        }
                    })
                    setFormLocked(true)
                    setCheckGiftCard(true)
                }
            }
        }
        setLoadingGiftCard(false)
    }
    const getBalancePrice = async () => {
        let time: any = null
        let cardPINTemp = cardPIN
        if (!showCardPIN) {
            setLoadingCount(true)
            const apolloClient = initializeApollo(null, {locale})
            const variablesGiftCardStatus = {
                GiftCardStatusInput: {
                    cardNumber,
                }
            }
            const checkStatusCard = await apolloClient.query({ query: GIFT_CARD_STATUS, variables: variablesGiftCardStatus }).then((res) => {
                if (res) {
                    const data = res?.data?.GiftCardStatus?.cardData
                    const isExpired = data?.is_expired
                    if (!data?.is_payment_active) {
                        toast({ description: 'Przykro nam, karta nie jest aktywna. Prosimy o kontakt z biurem obsługi klienta.', status: 'error' })
                    } else if (isExpired) {
                        toast({ description: 'Przykro nam, karta wygasła i nie jest dostępna, skorzystaj z innej.', status: 'error' })
                    } else if (data?.is_pin_required) {
                        setShowCardPIN(true)
                        return true
                    } else {
                        return false
                    }
                }
            })
            setLoadingCount(false)
            if (checkStatusCard) {
                return true
            } else {
                cardPINTemp = '0000'
                setCardPIN('0000')
            }
        }
        if (!cardNumber) {
            toast({ description: 'Uzupełnij numer karty podarunkowej.', status: 'warning' })
        }
        if (!cardPINTemp) {
            toast({ description: 'Uzupełnij pin karty podarunkowej.', status: 'warning' })
        }
        if (cardNumber && cardPINTemp) {
            time = setTimeout(async () => {
                setLoadingCount(true)
                const apolloClient = initializeApollo(null, {locale})
                const variablesGiftCardStatus = {
                    GiftCardStatusInput: {
                        cardNumber,
                    }
                }
                const checkStatusCard = await apolloClient.query({ query: GIFT_CARD_STATUS, variables: variablesGiftCardStatus }).then((res) => {
                    if (res) {
                        const data = res?.data?.GiftCardStatus?.cardData
                        const isExpired = data?.is_expired
                        if (!data?.is_payment_active) {
                            toast({ description: 'Przykro nam, karta nie jest aktywna. Prosimy o kontakt z biurem obsługi klienta.', status: 'error' })
                        } else if (isExpired) {
                            toast({ description: 'Przykro nam, karta wygasła i nie jest dostępna, skorzystaj z innej.', status: 'error' })
                        } else {
                            return true
                        }
                    }
                })
                if (checkStatusCard) {
                    const dataCart: any = await getCartDocument({
                        variables: {
                            cartId,
                        },
                    })
                    const priceOrder = dataCart?.data?.cart?.prices?.grand_total?.value || 0
                    if (Number(cardPrice) > Number(priceOrder)) {
                        toast({
                            description: 'Podana kwota jest za duża. Kwota powinna być równa wartości zamówienia.',
                            status: 'warning'
                        })
                    } else {
                        const variablesGiftCardBalance = {
                            GiftCardBalanceInput: {
                                cardNumber,
                                cardPin: cardPINTemp,
                            }
                        }
                        await apolloClient.query(({
                            query: GIFT_CARD_BALANCE,
                            variables: variablesGiftCardBalance
                        })).then(async (response) => {
                            if (response) {
                                const dataBalance = response?.data?.GiftCardBalance
                                const data = dataBalance?.cardData
                                const valueBalance = data?.value_balance
                                const success = dataBalance?.success
                                if (success) {
                                    setCardBalancePrice(valueBalance)
                                    setLoadingCount(false)
                                    setCountDisabled(true)
                                    if (Number(priceOrder) > valueBalance) {
                                        refCardPrice.current.value = valueBalance
                                        setCardPrice(valueBalance.toFixed(2))
                                    } else {
                                        refCardPrice.current.value = Number(priceOrder)
                                        setCardPrice(priceOrder.toFixed(2))
                                    }
                                } else {
                                    setLoadingCount(false)
                                    setCountDisabled(false)
                                    toast({description: 'PIN lub numer karty jest nieprawidłowy.', status: 'error'})
                                }
                            }
                        })
                    }
                }
            }, 100)
        }
        return () => clearTimeout(time)
    }
    const cancelAuthorizationCard = async () => {
        const apolloClient = initializeApollo(null, {locale})
        const variablesGiftCardCancel = {
            GiftCardCancelInput: {
                quoteMaskedId: cartId
            }
        }
        await apolloClient.query(({
            query: GIFT_CARD_CANCEL,
            variables: variablesGiftCardCancel
        })).then(async (response) => {
            if (response) {
                setCheckGiftCard(false)
                setCardNumber('')
                setCardPIN('')
                setCardPrice('')
                setCardBalance('')
                setCardBalancePrice('')
                setCardOrder(false)
                setCarrierCode(false)
                setShowCardPIN(false)
                setValue('selected_payment_method.code', undefined)
                setFormLocked(true)
                updateSummary()
            }
        })
    }
    useEffect(() => {
        if (selectPaymentMethodCode && selectPaymentMethodCode === 'giftCard') {
            setFormLocked(true)
            setCheckGiftCard(true)
        } else {
            setFormLocked(false)
            setCheckGiftCard(false)
        }
    }, [selectPaymentMethodCode])
    useEffect(() => {
        if (cardOrder === 'half' || cardOrder === 'full') {
            updateSummary()
        }
    }, [cardOrder])
    useEffect(() => {
        if (showCardPIN) {
            const inputPIN = document.getElementsByClassName('pinNumber')[0] as HTMLInputElement
            if (inputPIN) inputPIN.focus()
        }
    }, [showCardPIN])
    useEffect(() => {
        if (selectPaymentMethodCode) {
            const variables = {
                ecommerce: {
                    checkout: {
                        actionField: {
                            step: 4,
                            method: selectPaymentMethodCode,
                            action: 'checkout',
                        },
                        cartId,
                        products: data?.cart?.items,
                    }
                }
            }
            GTM_EVENTS.addPaymentInfo(variables)
        }
    }, [selectPaymentMethodCode])
    return (
        <>
            <Box mt="2.5rem">
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)} id="checkout[2]">
                        <Box mb={4}>
                            <Radio
                                name="selected_payment_method.code"
                                label={t('PAYMENT_METHOD_LABEL')}
                                options={availablePaymentMethods?.filter((el: any) => el?.code !== 'lilou_payment_giftcard')?.map((paymentMethod: any) => ({
                                    label: paymentMethod?.title,
                                    value: paymentMethod?.code,
                                }))}
                                isRequired={true}
                            />
                        </Box>
                        {
                            typeof cardOrder === 'string' && cardOrder?.length > 0 && cardOrder === 'half' && (
                                <span style={{ color: '#018001' }}>Wybierz uzupełniającą metodę płatności. Środki z karty podarunkowej pozostaną zarezerwowane do momentu opłacenia całej wartości zamówienia uzupełniającą metodą płatności.</span>
                            )
                        }
                        {
                            typeof cardOrder === 'string' && cardOrder?.length > 0 && cardOrder === 'full' && (
                                <span style={{ color: '#018001' }}>Środki z karty podarunkowej zostały zarezerwowane. Kwota zostanie pobrana po potwierdzeniu zamówienia.</span>
                            )
                        }
                        {
                            cardOrder !== 'full' && cardOrder !== 'half' && carrierCode !== 'flatrate2' && carrierCode !== 'inpostcourier' && (
                                <Box mt={4}>
                                    {
                                        availablePaymentMethods.some((method: any) => method.code === 'lilou_payment_giftcard') && (
                                            <Radio
                                                name="selected_payment_method.code"
                                                options={[{ label: 'Karta podarunkowa', value: 'giftCard'}]}
                                            />
                                        )
                                    }
                                    {
                                        checkGiftCard && (
                                            <>
                                                <FormControl isRequired={true} marginBottom={5}>
                                                    <FormLabel htmlFor={'cardNumber'}>Numer karty podarunkowej</FormLabel>
                                                    <InputGroup>
                                                        <Input
                                                            name="cardNumber"
                                                            placeholder="Podaj numer karty"
                                                            required
                                                            value={cardNumber?.toString()?.length > 0 ? cardNumber.toString() : ''}
                                                            onKeyUp={() => {
                                                                setCountDisabled(false)
                                                                setCardPIN('')
                                                                setShowCardPIN(false)
                                                                setCardPrice('')
                                                                setCardBalance('')
                                                                setCardBalancePrice('')
                                                                refCardPrice.current.value = ''
                                                            }}
                                                            onChange={(e) => {
                                                                setCountDisabled(false)
                                                                setCardNumber(e.target.value.trim())
                                                            }}
                                                            pr="3rem"
                                                        />
                                                        <InputRightElement pointerEvents="none" width="3.5rem">
                                                            <Icon as={FiCreditCard} />
                                                        </InputRightElement>
                                                    </InputGroup>
                                                </FormControl>
                                                <Box sx={{ display: 'flex', marginBottom: '20px', gap: '20px' }}>
                                                    {
                                                        showCardPIN && (
                                                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'strech' }}>
                                                                <FormControl isRequired={true} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
                                                                    <FormLabel htmlFor={'cardPIN'}>PIN do karty podarunkowej</FormLabel>
                                                                    <InputGroup>
                                                                        <Input
                                                                            className="pinNumber"
                                                                            name="cardPIN"
                                                                            placeholder="Podaj PIN"
                                                                            required
                                                                            type="password"
                                                                            value={cardPIN.toString()}
                                                                            onKeyUp={() => {
                                                                                setCountDisabled(false)
                                                                            }}
                                                                            onChange={(e) => {
                                                                                setCountDisabled(false)
                                                                                setCardPIN(e.target.value.trim())}
                                                                            }
                                                                            maxLength={4}
                                                                        />
                                                                        <InputRightElement width="3.5rem" pointerEvents="none">
                                                                            <Icon as={FiKey} />
                                                                        </InputRightElement>
                                                                    </InputGroup>
                                                                </FormControl>
                                                            </Box>
                                                        )
                                                    }
                                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'strech' }}>
                                                        <FormControl isRequired={true} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
                                                            <FormLabel htmlFor={'cardPrice'}>Jaką kwotę pobrać z karty podarunkowej?</FormLabel>
                                                            <InputGroup>
                                                                <Input
                                                                    ref={refCardPrice}
                                                                    name="cardPrice"
                                                                    placeholder="Podaj kwotę"
                                                                    required
                                                                    type="number"
                                                                    step={'0.01'}
                                                                    max={data?.cart?.prices?.grand_total?.value}
                                                                    min="0"
                                                                    disabled={!cardBalancePrice}
                                                                    onKeyUp={() => {
                                                                        setCountDisabled(false)
                                                                    }}
                                                                    onChange={(e) => {
                                                                        setCountDisabled(false)
                                                                        const value = e.target.value.trim()
                                                                        const formattedValue = value.includes('.') ? (Math.floor(Number(value) * 100) / 100).toFixed(2) : value

                                                                        if (value.includes('.') && value.split('.')[1]?.length > 2) {
                                                                            toast({
                                                                                description: `Wprowadzono więcej niż dwie cyfry po przecinku. Zapisano ${formattedValue}.`,
                                                                                status: 'warning'
                                                                            })
                                                                        }
                                                                        setCardPrice(formattedValue)
                                                                    }}
                                                                    pr="3.5rem"
                                                                />
                                                                <InputRightElement width="3.5rem" pointerEvents="none">
                                                                    zł
                                                                </InputRightElement>
                                                            </InputGroup>
                                                        </FormControl>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ marginTop:'10px', marginBottom: '20px' }}>
                                                    {
                                                        cardNumber && cardPIN && Number(cardBalance || cardBalancePrice) >= 0 ? (
                                                            <span>{(cardBalance || cardBalancePrice).toString()?.length > 0 ? 'Saldo karty: ' + Number(cardBalance || cardBalancePrice).toLocaleString('pl-PL', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2
                                                            }) + ' zł' : ''}</span>
                                                        ) : ''
                                                    }
                                                    <br/>
                                                    {
                                                        cardNumber && cardPIN && cardPrice && Number(cardBalance || cardBalancePrice) < data?.cart?.prices?.grand_total?.value ? (
                                                            <span style={{ color: '#a36d08' }}>Brakuje {(Number(data?.cart?.prices?.grand_total?.value) - Number(cardBalance || cardBalancePrice)).toLocaleString('pl-PL', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2
                                                            })} zł, by spłacić całe zamówienie.</span>
                                                        ) : ''
                                                    }
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                                    <Button
                                                        type="button"
                                                        variant={'outlined'}
                                                        isDisabled={countDisabled}
                                                        onClick={() => getBalancePrice()}
                                                        sx={{ border: '1px solid black' }}
                                                        width={{base: 'full', md: '20.625rem'}}
                                                    >
                                                        {
                                                            !cardPIN ? 'Sprawdź czy karta jest aktywna' : 'Sprawdź środki na karcie'
                                                        }
                                                        {
                                                            loadingCount && (
                                                                <CircularProgress sx={{marginLeft: '20px', transform: 'scale(.7)'}} isIndeterminate/>
                                                            )
                                                        }
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        isDisabled={(typeof cardBalancePrice === 'string' && cardBalancePrice?.length === 0) && (typeof cardBalance === 'string' && cardBalance?.length === 0)}
                                                        onClick={() => handleGifCard()}
                                                        width={{base: 'full', md: '20.625rem'}}
                                                    >
                                                        Zarezerwuj środki na karcie
                                                        {
                                                            loadingGiftCard && (
                                                                <CircularProgress sx={{marginLeft: '20px', transform: 'scale(.7)'}} isIndeterminate/>
                                                            )
                                                        }
                                                    </Button>
                                                </Box>
                                            </>
                                        )
                                    }
                                </Box>
                            )
                        }
                    </form>
                </FormProvider>
                <Box borderTop="1px solid #ddd" pt={6} mt={8} sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                    {
                        typeof cardOrder === 'string' && cardOrder?.length > 0 && (
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={() => cancelAuthorizationCard()}
                                width={{base: 'full', md: '20.625rem'}}
                                sx={{ marginTop: '10px', marginBottom: '10px', border: '1px solid black' }}
                            >
                                Anuluj blokadę środków karty
                            </Button>
                        )
                    }
                    <Button
                        width={{base: 'full', md: '20.625rem'}}
                        type="submit"
                        form="checkout[2]"
                        isDisabled={formLocked}
                        sx={{ margin: '10px 0'}}
                        isLoading={methods.formState.isSubmitting}
                    >
                        {t('CONFIRM_ORDER')}
                        {
                            formLocked && !checkGiftCard && (
                                <CircularProgress sx={{marginLeft: '20px', transform: 'scale(.7)'}} isIndeterminate/>
                            )
                        }
                    </Button>
                </Box>
            </Box>
        </>
    )
}

export default PaymentMethodForm
